import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { VariantColor } from '@repo/toolkit/colors'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { DebouncedInput } from '@repo/ui/debounced-input'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getCenterOlPayments } from '../../../../api/center-ol-payments/get-center-ol-payments'
import { getCenter } from '../../../../api/centers/get-center'
import { getCenterTotalCosts } from '../../../../api/centers/get-center-total-costs'
import { updateCenter } from '../../../../api/centers/update-center'
import { getOlPermissions } from '../../../../lib/constants/ol-permissions'
import { useObserver } from '../../../../lib/contexts/observer'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CenterOlPaymentTypeEnum } from '../../../../lib/types/enums/center-ol-payment/center-ol-payment-type.enum'
import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import { Center } from '../../../../lib/types/models/center/center'
import { DataProps } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { useCenterCosts } from './center.hooks'
import { CenterCostsTableProps } from './utils'

type CenterTotalCostsItemProps = {
  type: 'badge' | 'text'
  label: string
  value?: number
  currency: CurrencyEnum
  variant?: VariantColor
}

function CenterTotalCostsItem({
  type,
  label,
  value,
  currency,
  variant = 'orange'
}: CenterTotalCostsItemProps) {
  const formattedValue = value ? NumberFormatter.formatCurrency(value, currency) : '-'

  return (
    <div className={'w-full flex flex-row justify-between items-center'}>
      <Typography variant={'secondary-body'}>{label}</Typography>
      {type === 'badge' ? (
        <Badge text={formattedValue} variant={variant} />
      ) : (
        <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
          {formattedValue}
        </Typography>
      )}
    </div>
  )
}

type Props = Omit<CenterCostsTableProps, 'mode'>

export function CenterTotalCosts(props: DataProps<Center, Props>) {
  const { costType, currency } = props
  const { t } = useTranslate()
  const { userRole } = useAuth()
  const { id, data, isPending } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { centerCostsView } = useCenterCosts({ centerId: id, table: 'costs', costType })

  const { data: totalCosts, refetch: refetchTotalCosts } = useQuery({
    queryKey: ['center-grand-total-cost', id],
    queryFn: () => getCenterTotalCosts(id)
  })

  const { data: advanceOlPayments } = useQuery({
    queryKey: ['center-ol-payments', 'advance', `center-${id}`],
    queryFn: () =>
      getCenterOlPayments({ filter: { centerId: id, type: CenterOlPaymentTypeEnum.ADVANCE } })
  })

  const updateCenterMutation = useMutation({
    mutationKey: ['center-costs', 'update-retainer', `center-${id}`],
    mutationFn: (retainerAmount: number) => updateCenter({ id, retainerAmount }),
    onError: showAxiosError,
    onSuccess: async () => {
      await refetchTotalCosts()
      toast.success('Acompte mis à jour avec succès')
    }
  })

  useObserver({
    key: 'center-total-costs',
    update: async () => {
      await centerCostsView.refetch()
    }
  })

  const olPermissions = getOlPermissions({ role: userRole, type: data?.ol?.type })

  const totalCost = costType === 'final-cost' ? totalCosts?.finalTotalCost : totalCosts?.totalCost
  const balance = costType === 'final-cost' ? totalCosts?.finalBalance : totalCosts?.balance

  if (!centerCostsView.data || !totalCosts) return null
  return (
    <Box className={'p-0 flex flex-col gap-3 bg-white-hover'}>
      <CenterTotalCostsItem
        type={'badge'}
        label={t('center.costs.totals.grand.total')}
        value={totalCost}
        currency={currency}
      />

      {advanceOlPayments?.data &&
        advanceOlPayments.data.length > 0 &&
        advanceOlPayments.data.map((payment) => (
          <CenterTotalCostsItem
            key={payment.id}
            type={'text'}
            label={
              payment.validatedAt
                ? `${payment.label} - ${DateFormatter.DDMMYYYY(payment.validatedAt)}`
                : payment.label
            }
            value={payment.amount * -1}
            currency={payment.currency}
          />
        ))}

      <div className={'w-full flex flex-row justify-between items-center'}>
        <Typography variant={'secondary-body'}>Retainer</Typography>

        {olPermissions['center-update-retainer-cost'] ? (
          <DebouncedInput
            label="Retainer"
            type="number"
            labelClassName="bg-white-hover"
            className="w-24"
            disabled={isPending}
            value={data?.retainerAmount}
            onChange={async (e) => await updateCenterMutation.mutateAsync(Number(e.target.value))}
          />
        ) : (
          <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
            -
            {valueRender(data?.retainerAmount, (v) => NumberFormatter.formatCurrency(v, currency))}
          </Typography>
        )}
      </div>

      <div className={'border-t border-dashed border-grey-light-hover'} />
      <CenterTotalCostsItem
        type={'badge'}
        label={t('center.costs.balance')}
        value={balance}
        currency={currency}
        variant={'primary'}
      />
    </Box>
  )
}
